/**
 * PaymentCardFormWrapper component is responsible for handling a payment card form.
 * It provides logic for fetching the iframe URL for the payment card form and abstracts the handling of messages from the FDMS iframe.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child components.
 * @param {Object} props.formikProps - The Formik props for form handling.
 *
 * @example
 * <PaymentCardFormWrapper formikProps={formikProps}>
 *   <TxPaymentCardForm
 *       billingAddressTitle={<Trans file="Payment" id="BillingAddressTitle" />}
 *       paymentMethodTitle={<Trans file="Payment" id="CardInformationTitle" />}
 *       mode={TxPaymentCardFormMode.Edit}
 *   />
 * </PaymentCardFormWrapper>
 *
 * @returns {React.Element} The cloned child element with injected props for handling iframe URL.
 */

import React, { Children, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Vendors
import { useDispatch, useSelector } from 'react-redux';
import { TxPaymentCardFormMode } from 'texkit-ui/forms';

// Actions
import { getPaymentCardFrameUrl } from '../../../reducers/payment/paymentActions';

// Helpers
import { getIsAmexAccepted } from '../../../lib/helpers';
import ERRORS from '../../../lib/validation/errorCopy';

// Selectors
import { selectAppSlugByPath } from '../../../reducers/agency/agencySelectors';

const findChildByType = (children, type) => {
  const childArray = Children.toArray(children);
  return childArray.find(child => {
    return (
      child.type && [child.type.name, child.props.displayName].includes(type)
    );
  });
};

const PaymentCardFormWrapper = ({ children, formikProps }) => {
  const [iFrameUrl, setIFrameUrl] = useState('about:blank');
  const dispatch = useDispatch();
  const currentAppSlug = useSelector(selectAppSlugByPath);

  const fetchIframeUrl = async () => {
    const url = await dispatch(getPaymentCardFrameUrl());
    setIFrameUrl(url);
  };

  useEffect(() => {
    const paymentForm = findChildByType(children, 'TxPaymentCardForm');
    if (paymentForm.props.mode !== TxPaymentCardFormMode.Edit) {
      fetchIframeUrl();
    }
  }, []);

  const acceptedCards = [
    'VISA',
    'MasterCard',
    'Discover',
    getIsAmexAccepted(currentAppSlug) && 'American Express',
  ].filter(Boolean);

  const handleFieldBlur = event => {
    const paymentForm = findChildByType(children, 'TxPaymentCardForm');
    const fieldName = event.target.name;

    formikProps.setFieldTouched(fieldName, true);
    if (paymentForm.props.onFieldBlur) {
      paymentForm.props.onFieldBlur(event);
    }
  };

  const handleCardFieldMessage = data => {
    let fdmsMessage = {};

    if (data.success) {
      // First check if the agency accepts AMEX
      if (data.cardtype === 'AMEX' && !getIsAmexAccepted(currentAppSlug)) {
        formikProps.setFieldError(
          'cardNumber',
          ERRORS.FIELDS.CREDITCARD.NUMBER.AMEX_NOT_ACCEPTED
        );
      } else {
        fdmsMessage = data;
        // Clear any previous error for cardNumber
        formikProps.setFieldError('cardNumber');
      }
    } else if (data.success === false) {
      formikProps.setFieldError(
        'cardNumber',
        ERRORS.FIELDS.CREDITCARD.NUMBER.VALID
      );
    }

    const paymentForm = findChildByType(children, 'TxPaymentCardForm');
    if (paymentForm.props.onCardFieldMessage) {
      paymentForm.props.onCardFieldMessage(fdmsMessage);
    }
  };

  return (
    <>
      {Children.map(children, child => {
        if (child.props.displayName === 'TxPaymentCardForm') {
          return React.cloneElement(child, {
            acceptedCards,
            iFrameUrl,
            onFieldChange: formikProps.handleChange,
            onFieldBlur: handleFieldBlur,
            onCardFieldMessage: handleCardFieldMessage,
            values: formikProps.values,
          });
        }
        return child;
      })}
    </>
  );
};

PaymentCardFormWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  formikProps: PropTypes.shape({
    handleChange: PropTypes.func.isRequired,
    values: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    setFieldError: PropTypes.func,
    setFieldTouched: PropTypes.func,
  }).isRequired,
};

export default PaymentCardFormWrapper;
